import { EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectBookingByBookingID } from 'reduxStore/bookings/selectors';
import { bookingActions } from 'reduxStore/bookings/slice';
import { selectLockersDevices } from 'reduxStore/devices/selectors';
import { selectPeople } from 'reduxStore/people/selectors';
import { Button, DatePicker, Flex, Form, Input, Modal, Select, Tooltip, Typography } from 'zui/Antd';
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const UpdateBookingModal = ({ children, open, setOpen, bookingID }) => {
	const {
		token: { paddingLG, paddingXL }
	} = theme.useToken();
	const dispatch = useDispatch();
	const [form] = Form.useForm();

	const booking = useSelector((state) => selectBookingByBookingID(state, bookingID));
	const devices = useSelector((state) => selectLockersDevices(state));
	const people = useSelector((state) => selectPeople(state));
	const { updateBookingLoading: loading, updateBookingComplete } = useSelector((state) => state.Bookings);

	// Pre-fill form
	useEffect(() => {
		if (booking && open) {
			form.setFieldsValue({
				name: booking.name,
				personID: booking.assignedToPerson,
				deviceID: booking.deviceID,
				schedule: [dayjs(booking.startTime), dayjs(booking.endTime)]
			});
		}
	}, [booking, open, form]);

	const handleCancel = useCallback(() => {
		setOpen(false);
		form.resetFields();
	}, [form, setOpen]);

	useEffect(() => {
		if (updateBookingComplete) {
			handleCancel();
			dispatch(bookingActions.resetUpdateBookingState());
		}
	}, [updateBookingComplete, handleCancel, dispatch]);

	const handleSubmit = (values) => {
		const startDate = values.schedule[0].toDate();
		const endDate = values.schedule[1].toDate();

		const now = new Date();
		const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		const parsedStartDate = new Date(startDate);
		const parsedEndDate = new Date(endDate);

		if (parsedStartDate.getTime() === today.getTime()) {
			// If the start date is today, use current time
			parsedStartDate.setHours(now.getHours(), now.getMinutes(), 0, 0);
		} else {
			// If it's another day, use opening time (optional)
			parsedStartDate.setHours(5, 55, 0, 0);
		}

		// Set end date to the end of the day
		parsedEndDate.setHours(23, 59, 0, 0);

		const payload = {
			name: values.name,
			assignedToPerson: values.personID,
			deviceID: values.deviceID,
			startTime: parsedStartDate.toISOString(),
			endTime: parsedEndDate.toISOString()
		};

		dispatch(
			bookingActions.updateBooking({
				bookingID: bookingID,
				data: payload
			})
		);
	};

	return (
		<>
			<Tooltip title='Update Booking' placement='top'>
				{children ? (
					children
				) : (
					<Button icon={<EditOutlined />} onClick={() => setOpen(true)} loading={loading}>
						Update Booking
					</Button>
				)}
			</Tooltip>

			<Modal
				open={open}
				onCancel={handleCancel}
				centered
				closeIcon={false}
				width={716}
				destroyOnClose={true}
				styles={{
					content: {
						padding: `${paddingLG}px ${paddingXL}px`
					}
				}}
				footer={[
					<Button onClick={handleCancel} type='default' style={{ width: 121 }}>
						Cancel
					</Button>,
					<Button
						form={form}
						key='submit'
						htmlType='submit'
						type='primary'
						style={{ width: 121 }}
						loading={loading}
					>
						Update
					</Button>
				]}
			>
				<Flex vertical gap={30}>
					<Flex align='center' gap={4}>
						<Title style={{ margin: 0 }} level={4}>
							Update Booking
						</Title>
					</Flex>

					<Form id={form} form={form} layout='vertical' onFinish={handleSubmit}>
						<Flex vertical gap={16}>
							<Form.Item
								label={<Text strong>Booking Name</Text>}
								name='name'
								rules={[{ required: true, message: 'Please enter a booking name' }]}
								style={{ width: '75%', margin: 0 }}
								required={false}
							>
								<Input placeholder='e.g. Booking 236' />
							</Form.Item>

							<Flex justify='space-between' gap={16}>
								<Form.Item
									label={<Text strong>User</Text>}
									name='personID'
									rules={[{ required: true, message: 'Please select a user' }]}
									style={{ width: '100%', margin: 0 }}
									required={false}
								>
									<Select
										disabled
										placeholder='Select User'
										showSearch
										optionFilterProp='label'
										options={people?.map((person) => ({
											value: person.personID,
											label:
												person.firstName && person.lastName
													? `${person.firstName} ${person.lastName}`
													: person.displayName || person.email
										}))}
									/>
								</Form.Item>

								<Form.Item
									label={<Text strong>Device</Text>}
									name='deviceID'
									rules={[{ required: true, message: 'Please select a device' }]}
									style={{ width: '100%', margin: 0 }}
									required={false}
								>
									<Select
										placeholder='Select Device'
										showSearch
										optionFilterProp='label'
										options={devices.map((device) => ({
											value: device.deviceID,
											label: device.name
										}))}
										disabled
									/>
								</Form.Item>
							</Flex>

							<Form.Item
								label={<Text strong>Schedule</Text>}
								name='schedule'
								rules={[{ required: true, message: 'Please select a schedule' }]}
								style={{ margin: 0 }}
								required={false}
							>
								<RangePicker
									style={{ marginBottom: '8px' }}
									placeholder={['Start Date', 'End Date']}
									minDate={dayjs()}
									format={'DD/MM/YYYY'}
								/>
							</Form.Item>
						</Flex>
					</Form>
				</Flex>
			</Modal>
		</>
	);
};

export default UpdateBookingModal;
